<script lang="ts" setup>
import { RouterView } from 'vue-router';
import SuspendedErrorHandler from '@/components/suspense/SuspendedErrorHandler.vue';
import ApplicationWrapper from '@/components/layouts/ApplicationWrapper.vue';
import { useI18n } from 'vue-i18n';
import useSnackbar from '@/composables/useSnackbar';
import { useDisplay } from 'vuetify';
import SiteLogo from '@/components/SiteLogo.vue';
import { VueQueryDevtools } from '@tanstack/vue-query-devtools';
import { computed } from 'vue';
import type { SnackbarAction } from './types/composables/useSnackbar';
import useIsMobilePlatform from './composables/useIsMobilePlatform';

const { t, te } = useI18n();
const { snackbars, removeSnackbar } = useSnackbar();
const { smAndDown } = useDisplay();

const { provideIsMobilePlatform } = useIsMobilePlatform();
provideIsMobilePlatform();

const hasSnackbars = computed(() => snackbars.value.some(({ enabled }) => enabled));
const cleanSnackbarActionBind = (
  action: SnackbarAction
): Omit<SnackbarAction, 'callback' | 'actionLabel' | 'dataTestId'> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- we want to exclude these from the returned object
  const { callback: _, actionLabel: __, dataTestId: ___, ...bind } = action;
  return bind;
};
</script>

<template>
  <VueQueryDevtools />
  <div
    v-if="hasSnackbars"
    :class="['snackbar-container', { 'snackbar-container--mobile': smAndDown }]"
  >
    <VSnackbar
      v-for="snackbar in snackbars"
      :key="snackbar.key"
      v-model="snackbar.enabled"
      class="snackbar-item"
      contained
      v-bind="snackbar"
    >
      <v-icon v-if="snackbar.icon" class="block" size="x-large">
        {{ snackbar.icon }}
      </v-icon>
      {{ snackbar.message }}

      <CloseBtn
        :aria-label="t('common.close')"
        class="snackbar-item__close-btn pa-0"
        color="on-surface"
        data-test-id="snackbar-item-close-icon"
        icon
        size="24"
        @click="removeSnackbar(snackbar.key ?? '')"
      >
        <v-icon>mdi-close</v-icon>
      </CloseBtn>

      <template #actions>
        <v-btn
          v-for="action in snackbar.actions"
          :key="`${snackbar.key}_action_${action.key}`"
          v-bind="cleanSnackbarActionBind(action)"
          :data-test-id="action.dataTestId"
          @click.stop="() => action.callback(snackbar.key)"
        >
          {{ action.actionLabel }}
        </v-btn>
      </template>
    </VSnackbar>
  </div>

  <SuspendedErrorHandler :dont-catch-network-errors="true">
    <template #alertContainer>
      <v-app>
        <v-container class="app__error">
          <SiteLogo />
          <VAlert class="app__error-alert" elevation="1" outlined type="error" sentry-unmask>
            {{
              te('errors.app.failedToRender')
                ? t('errors.app.failedToRender')
                : 'There was an error loading Webmail. Please try reloading the page'
            }}
          </VAlert>
        </v-container>
      </v-app>
    </template>
    <ApplicationWrapper>
      <RouterView />
    </ApplicationWrapper>
  </SuspendedErrorHandler>
</template>

<style lang="scss">
.snackbar-container {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 100%;
  max-width: 26.5rem;
  height: 100%;
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  pointer-events: none;
  gap: 0.5rem;

  &--mobile {
    right: 0;
    flex-direction: column-reverse;
  }
}

.snackbar-item {
  position: relative;
  overflow-wrap: break-word;
  pointer-events: all;
  margin: 0 0.5rem;

  .v-snackbar__wrapper {
    position: relative;
    height: 100%;
    width: 100%;
    min-width: unset;

    &.bg-alert-error {
      border: 1px solid rgb(var(--v-theme-error-graphic));

      .v-icon {
        color: rgb(var(--v-theme-error-graphic));
      }
    }

    &.bg-warning {
      border: 1px solid rgb(var(--v-theme-warning-graphic));

      .v-icon {
        color: rgb(var(--v-theme-warning-graphic));
      }
    }

    &.bg-info {
      border: 1px solid rgb(var(--v-theme-info-graphic));

      .v-icon {
        color: rgb(var(--v-theme-info-graphic));
      }
    }

    &.bg-success {
      border: 1px solid rgb(var(--v-theme-success-graphic));

      .v-icon {
        color: rgb(var(--v-theme-success-graphic));
      }
    }
  }

  .v-snackbar__content {
    padding: 0.625rem 1rem;
    gap: 1rem;
    display: flex;
    align-items: center;
  }

  &__close-btn {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
  }
}

.v-btn.v-btn--close.snackbar-item__close-btn {
  padding: 0 !important;
}

.app__error {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  gap: 1rem;
}

.app__error-alert {
  flex: none;
}
</style>
